import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import './SettingsModal.css';

const SettingsModal = ({ 
  isOpen, 
  onClose, 
  settings, 
  onSettingsChange,
  onMicrophoneChange,
  onSpeakerChange,
  availableAudioDevices,
  availableVideoDevices,
  selectedAudioDevice,
  selectedVideoDevice
}) => {
  const { isDarkTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('voice');
  
  // Local state for settings
  const [localSettings, setLocalSettings] = useState({
    voice: {
      noiseCancellation: true,
      advancedNoiseSuppression: false,
      echoCancellation: true,
      outputVolume: 100
    },
    video: {
      mirrorVideo: true,
      videoBrightness: 100
    },
    devices: {
      selectedMicrophone: 'default',
      selectedSpeaker: 'default'
    }
  });

  // Initialize settings from props
  useEffect(() => {
    if (settings) {
      setLocalSettings(settings);
    }
  }, [settings]);

  // Update selected devices in local settings when props change
  useEffect(() => {
    if (selectedAudioDevice) {
      setLocalSettings(prev => ({
        ...prev,
        devices: {
          ...prev.devices,
          selectedMicrophone: selectedAudioDevice.deviceId
        }
      }));
    }
  }, [selectedAudioDevice]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleToggle = (section, setting) => {
    const newSettings = {
      ...localSettings,
      [section]: {
        ...localSettings[section],
        [setting]: !localSettings[section][setting]
      }
    };
    setLocalSettings(newSettings);
    
    // Immediately apply the changes
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  const handleSliderChange = (section, setting, value) => {
    const newValue = parseInt(value);
    const newSettings = {
      ...localSettings,
      [section]: {
        ...localSettings[section],
        [setting]: newValue
      }
    };
    setLocalSettings(newSettings);
    onSettingsChange?.(newSettings);
    
    // Apply changes immediately
    if (section === 'voice' && setting === 'outputVolume') {
      // Apply volume immediately to all audio/video elements
      const audioElements = document.querySelectorAll('audio, video');
      audioElements.forEach(element => {
        element.volume = newValue / 100;
      });
    } else if (section === 'video' && setting === 'videoBrightness') {
      // Apply brightness immediately to all video elements
      const videoElements = document.querySelectorAll('video');
      videoElements.forEach(video => {
        video.style.filter = `brightness(${newValue}%)`;
      });
    }
  };

  const handleDeviceChange = (deviceType, value) => {
    const newSettings = {
      ...localSettings,
      devices: {
        ...localSettings.devices,
        [deviceType]: value
      }
    };
    setLocalSettings(newSettings);
    onSettingsChange?.(newSettings);

    // Call the appropriate device change handler
    if (deviceType === 'selectedMicrophone' && onMicrophoneChange) {
      const device = availableAudioDevices?.find(d => d.deviceId === value);
      if (device) {
        onMicrophoneChange(device);
      }
    } else if (deviceType === 'selectedSpeaker' && onSpeakerChange) {
      // Handle speaker change if supported
      onSpeakerChange(value);
    }
  };


  const renderTabContent = () => {
    switch (activeTab) {
      case 'voice':
        return (
          <div className="settings-content">
            <h2 className="settings-section-title">Voice Settings</h2>
            
            <div className="settings-item">
              <div className="settings-item-info">
                <label className="settings-label">Noise cancellation</label>
                <p className="settings-description">Remove background noise from your audio input to improve the call quality.</p>
              </div>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="noise-cancellation"
                  checked={localSettings.voice.noiseCancellation}
                  onChange={() => handleToggle('voice', 'noiseCancellation')}
                />
                <label htmlFor="noise-cancellation" className="toggle-label"></label>
              </div>
            </div>

            <div className="settings-item">
              <div className="settings-item-info">
                <label className="settings-label">Advanced noise suppression</label>
                <p className="settings-description">Enhanced AI-powered noise suppression for better audio quality.</p>
              </div>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="advanced-noise"
                  checked={localSettings.voice.advancedNoiseSuppression}
                  onChange={() => handleToggle('voice', 'advancedNoiseSuppression')}
                />
                <label htmlFor="advanced-noise" className="toggle-label"></label>
              </div>
            </div>

            <div className="settings-item">
              <div className="settings-item-info">
                <label className="settings-label">Echo cancellation</label>
                <p className="settings-description">Remove sound echo from your audio input to improve the call quality.</p>
              </div>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="echo-cancellation"
                  checked={localSettings.voice.echoCancellation}
                  onChange={() => handleToggle('voice', 'echoCancellation')}
                />
                <label htmlFor="echo-cancellation" className="toggle-label"></label>
              </div>
            </div>

            <div className="settings-item">
              <div className="settings-item-info">
                <label className="settings-label">Output volume</label>
                <p className="settings-description">Adjust the output volume for speakers and audio playback. ({localSettings.voice.outputVolume}%)</p>
              </div>
              <div className="slider-container">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={localSettings.voice.outputVolume}
                  onChange={(e) => handleSliderChange('voice', 'outputVolume', e.target.value)}
                  className="settings-slider"
                />
                <span className="slider-value">{localSettings.voice.outputVolume}%</span>
              </div>
            </div>
          </div>
        );

      case 'video':
        return (
          <div className="settings-content">
            <h2 className="settings-section-title">Video Settings</h2>
            
            <div className="settings-item">
              <div className="settings-item-info">
                <label className="settings-label">Mirror video</label>
                <p className="settings-description">Flip your video horizontally to correct the orientation.</p>
              </div>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="mirror-video"
                  checked={localSettings.video.mirrorVideo}
                  onChange={() => handleToggle('video', 'mirrorVideo')}
                />
                <label htmlFor="mirror-video" className="toggle-label"></label>
              </div>
            </div>

            <div className="settings-item">
              <div className="settings-item-info">
                <label className="settings-label">Video brightness</label>
                <p className="settings-description">Adjust the brightness of your video to improve visibility. ({localSettings.video.videoBrightness}%)</p>
              </div>
              <div className="slider-container">
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={localSettings.video.videoBrightness}
                  onChange={(e) => handleSliderChange('video', 'videoBrightness', e.target.value)}
                  className="settings-slider"
                />
                <span className="slider-value">{localSettings.video.videoBrightness}%</span>
              </div>
            </div>
          </div>
        );

      case 'devices':
        return (
          <div className="settings-content">
            <h2 className="settings-section-title">Device Settings</h2>
            
            <div className="settings-item">
              <div className="settings-item-info">
                <label className="settings-label">Microphone Device</label>
                <p className="settings-description">Select the audio input device for microphone and voice recording.</p>
              </div>
              <div className="device-selector">
                <select
                  value={localSettings.devices.selectedMicrophone}
                  onChange={(e) => handleDeviceChange('selectedMicrophone', e.target.value)}
                  className="settings-select"
                >
                  {availableAudioDevices && availableAudioDevices.length > 0 ? (
                    availableAudioDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || device.name || `Microphone ${device.deviceId?.slice(0, 5)}...`}
                      </option>
                    ))
                  ) : (
                    <option value="default">Default Microphone</option>
                  )}
                </select>
              </div>
            </div>

            <div className="settings-item">
              <div className="settings-item-info">
                <label className="settings-label">Speaker Device</label>
                <p className="settings-description">Select the audio output device for speakers and audio playback.</p>
              </div>
              <div className="device-selector">
                <select
                  value={localSettings.devices.selectedSpeaker}
                  onChange={(e) => handleDeviceChange('selectedSpeaker', e.target.value)}
                  className="settings-select"
                >
                  {(() => {
                    // Get speaker devices from available devices
                    if (navigator.mediaDevices && 'selectAudioOutput' in HTMLMediaElement.prototype) {
                      // Browser supports speaker selection
                      return (
                        <>
                          <option value="default">Default Speaker</option>
                          <option value="communications">Communications Device</option>
                        </>
                      );
                    } else {
                      return <option value="default">Default Speaker (Not configurable in this browser)</option>;
                    }
                  })()}
                </select>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`settings-modal-overlay ${isDarkTheme ? 'dark' : ''}`} onClick={onClose}>
      <div className="settings-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="settings-modal-header">
          <h1 className="settings-title">Settings</h1>
          <button className="settings-close-button" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="settings-modal-body">
          <div className="settings-tabs">
            <button 
              className={`settings-tab ${activeTab === 'voice' ? 'active' : ''}`}
              onClick={() => handleTabClick('voice')}
            >
              <svg className="tab-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C11.4477 2 11 2.44772 11 3V11C11 11.5523 11.4477 12 12 12C12.5523 12 13 11.5523 13 11V3C13 2.44772 12.5523 2 12 2Z" fill="currentColor"/>
                <path d="M8 6C7.44772 6 7 6.44772 7 7V11C7 11.5523 7.44772 12 8 12C8.55228 12 9 11.5523 9 11V7C9 6.44772 8.55228 6 8 6Z" fill="currentColor"/>
                <path d="M16 6C15.4477 6 15 6.44772 15 7V11C15 11.5523 15.4477 12 16 12C16.5523 12 17 11.5523 17 11V7C17 6.44772 16.5523 6 16 6Z" fill="currentColor"/>
              </svg>
              Voice
            </button>
            <button 
              className={`settings-tab ${activeTab === 'video' ? 'active' : ''}`}
              onClick={() => handleTabClick('video')}
            >
              <svg className="tab-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 10L21 7V17L16 14V10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="2" y="6" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Video
            </button>
            <button 
              className={`settings-tab ${activeTab === 'devices' ? 'active' : ''}`}
              onClick={() => handleTabClick('devices')}
            >
              <svg className="tab-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15.5C14.21 15.5 16 13.71 16 11.5V6C16 3.79 14.21 2 12 2C9.79 2 8 3.79 8 6V11.5C8 13.71 9.79 15.5 12 15.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 11.5C19 15.64 15.64 19 11.5 19M11.5 19C7.36 19 4 15.64 4 11.5M11.5 19V22M11.5 22H8.5M11.5 22H14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Devices
            </button>
          </div>

          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
